import {
  AprState,
  asPrecisePct,
  asWholePct,
  getCurrentUtilizationDescription,
  getNewUtilizationDescription,
  isNotNullNorUndefined,
  normalizeValue,
  pointsToPrecisePctWithDecimals,
  UtilizationState,
} from '@silo/utils';
import { SiloAsset } from 'app/queries/decoratedSilo';
import { useQueryGraphInterestRates } from 'app/queries/interest-rate/graph';
import {
  useChartDataForDayRangeAndAsset,
  useInterestRatesChartData,
} from 'app/queries/interest-rate/interest-rates-chart-data';
import { useQueryInterestRateModelConfig } from 'app/queries/siloAssetInterestRateModel';
import { flow, identity } from 'fp-ts/lib/function';
import { FC, useId, useMemo, useState } from 'react';
import { calculateTrends } from '../../charts/apy-chart-trend';
import { ApyChart, ApyChartData } from '../../charts/ApyChart';
import { ApyChartLegend } from '../../charts/ApyChartLegend';
import { ChartDateRange, ChartRangeToggle, isHourlyChartDateRange } from '../../charts/ChartRangeToggle';
import { Legend, LegendItem } from '../../charts/LegendItems';
import { UtilizationChart as RawUtilizationChart } from '../../charts/UtilizationChart';
import { PulseAPYChart, PulseUtilizationChart } from '../PulsePanel';
import { ChecksumAddress } from '@silo/types';
import { getIrmMeta } from '../../charts/utils';
import { StaticDescription, TooltipDescription } from '@silo/ui';
import { useThisDecoratedSilo } from '../../silo';
import { useActionFormContext } from './ActionForm.context';

const toChartValue = flow(normalizeValue, (value) => value * 100);

export type MapAPYData = (data: ApyChartData) => ApyChartData;
export type GetAverageAPY = (apyData: ApyChartData) => number;

type HistoricalApyChartProps = {
  asset: SiloAsset;
  siloAddress: ChecksumAddress;
  getAverageAPY: GetAverageAPY;
  mapAPYData?: MapAPYData;
};

export const HistoricalApyChart: FC<HistoricalApyChartProps> = ({
  asset,
  mapAPYData = identity,
  getAverageAPY,
  siloAddress,
}) => {
  const id = useId();
  const [selectedDayRange, setSelectedDayRange] = useState<ChartDateRange>(7);
  const interestRatesQuery = useQueryGraphInterestRates(siloAddress);
  const byAsset = useInterestRatesChartData(interestRatesQuery.data);
  const data = useChartDataForDayRangeAndAsset(byAsset, asset.assetAddress, selectedDayRange);
  const mappedData = useMemo(() => (data ? mapAPYData(data) : data), [data, mapAPYData]);
  const average = useMemo(() => {
    if (!data) return data;

    return getAverageAPY(data);
  }, [data, getAverageAPY]);
  const trends = useMemo(() => (mappedData ? calculateTrends(mappedData) : undefined), [mappedData]);

  return (
    <>
      {mappedData ? (
        <div className="flex flex-col gap-5">
          <h3 className="text-lg font-medium text-white">{asset.symbol} APR</h3>
          <div className="flex justify-between">
            <ChartRangeToggle range={selectedDayRange} onChange={setSelectedDayRange} id={id} />

            <ApyChartLegend
              depositAPY={
                mappedData.some((d) => isNotNullNorUndefined(d.depositApy))
                  ? asPrecisePct(asset.depositAPY.toBigInt())
                  : undefined
              }
              borrowAPY={
                mappedData.some((d) => isNotNullNorUndefined(d.borrowApy))
                  ? asPrecisePct(asset.borrowAPY.toBigInt())
                  : undefined
              }
              averageAPY={average ? pointsToPrecisePctWithDecimals(average) : undefined}
              trend={trends}
              hourly={isHourlyChartDateRange(selectedDayRange)}
            />
          </div>
          <div>
            <ApyChart data={mappedData} averageAPY={average} hourly={isHourlyChartDateRange(selectedDayRange)} />
          </div>
        </div>
      ) : !interestRatesQuery.error ? (
        <PulseAPYChart />
      ) : null}
    </>
  );
};

type UtilizationChartProps = {
  newUtilization: bigint | null;
  currentAprState: AprState | null;
  currentUtilizationState: UtilizationState;
  newUtilizationState: UtilizationState | undefined;
};

export const UtilizationChart: FC<UtilizationChartProps> = ({
  newUtilization,
  currentAprState,
  currentUtilizationState,
  newUtilizationState,
}) => {
  const { siloAddress } = useThisDecoratedSilo();
  const { selectedAsset } = useActionFormContext();
  const interestRateModelConfig = useQueryInterestRateModelConfig(siloAddress, selectedAsset.assetAddress);

  const chartValues = interestRateModelConfig.data
    ? {
        current: toChartValue(selectedAsset.utilization),
        low: toChartValue(interestRateModelConfig.data.ulow),
        optimal: toChartValue(interestRateModelConfig.data.uopt),
        critical: toChartValue(interestRateModelConfig.data.ucrit),
        new: isNotNullNorUndefined(newUtilization) ? toChartValue(newUtilization) : undefined,
      }
    : undefined;

  const { data: config } = useQueryInterestRateModelConfig(siloAddress, selectedAsset.assetAddress);

  const { hasLow, hasOptimal } = getIrmMeta(config);

  const currentDescription = getCurrentUtilizationDescription(currentAprState, currentUtilizationState);
  const newDescription = getNewUtilizationDescription(currentUtilizationState, newUtilizationState);
  const utilizationDescription = newDescription ?? currentDescription;

  return (
    <>
      {chartValues ? (
        <div className="flex flex-col gap-6">
          <div className="flex justify-between ">
            <div className="flex gap-1.5 items-center">
              <h3 className="text-lg font-bold text-white">{selectedAsset.symbol} Utilization</h3>

              <TooltipDescription>{utilizationDescription}</TooltipDescription>
            </div>
            {selectedAsset ? (
              <Legend>
                <LegendItem
                  value={asWholePct(selectedAsset.utilization)}
                  label="Current utilization"
                  align="center"
                  color="bg-white"
                />
                {newUtilization ? (
                  <LegendItem
                    value={asWholePct(newUtilization)}
                    label="New utilization"
                    align="center"
                    color="bg-violet-400"
                  />
                ) : null}
              </Legend>
            ) : null}
          </div>

          <StaticDescription>{utilizationDescription}</StaticDescription>

          <div className="px-3 pb-4">
            <RawUtilizationChart data={chartValues} hasLow={hasLow} hasOptimal={hasOptimal} />
          </div>
        </div>
      ) : !interestRateModelConfig.error ? (
        <PulseUtilizationChart />
      ) : null}
    </>
  );
};
